/**
 * « Mes cadeaux » (`/cadeaux`, migration 0326) : les cartes cadeaux achetées,
 * leur code, un QR code, et les moyens de les transmettre (partage, email,
 * copie). Au retour de Stripe (`?purchase=`), la page attend que le webhook
 * ait créé le code.
 */

import { useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Check, Copy, Gift, Mail, Share2 } from 'lucide-react';
import { Button, MathText } from '@jeraisonne/ui';
import { QrCodeImage } from '@/components/qr-code';
import { useAuth } from '@/lib/auth';
import { copyText } from '@/lib/clipboard';
import { shareContent } from '@/lib/native-share';
import {
  formatGiftCode,
  giftLotLabel,
  giftMailto,
  giftShareText,
  giftUrl,
  useMyGifts,
  type SkillcardGift,
} from '@/lib/skillcards/gifts';

export function GiftsPage() {
  const { user } = useAuth();
  const search = useSearch({ strict: false }) as { purchase?: string };
  const purchaseId = typeof search.purchase === 'string' ? search.purchase : null;
  const gifts = useMyGifts(user?.id, !!purchaseId);
  const list = gifts.data ?? [];
  const waiting = !!purchaseId && !list.some((g) => g.purchase_id === purchaseId);

  return (
    <div className="mx-auto max-w-lg space-y-5 px-4 py-6 pb-24 sm:py-8 lg:max-w-2xl">
      <Link
        to="/"
        className="text-primary-600 inline-flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Accueil
      </Link>
      <div className="space-y-1">
        <h1 className="font-display text-ink-900 flex items-center gap-2 text-2xl font-extrabold">
          <Gift className="text-accent-500 h-6 w-6" aria-hidden /> Mes cadeaux
        </h1>
        <p className="text-ink-600 text-sm">
          Transmets chaque code à la personne de ton choix : elle l’active sur JeRaisonne et les
          SKILLCARDS rejoignent son deck.
        </p>
      </div>

      {waiting && (
        <p role="status" className="bg-primary-50 text-primary-800 rounded-2xl p-4 text-sm">
          Paiement reçu, création du code cadeau…
        </p>
      )}

      {gifts.isLoading ? (
        <div className="bg-ink-100 h-40 animate-pulse rounded-2xl" />
      ) : list.length === 0 && !waiting ? (
        <p className="border-ink-100 text-ink-500 rounded-2xl border border-dashed p-6 text-center text-sm">
          Aucune carte cadeau pour l’instant. Ouvre une SKILLCARD, puis « Débloquer » → « Offrir à
          quelqu’un ».
        </p>
      ) : (
        <ul className="space-y-4">
          {list.map((g) => (
            <li key={g.id}>
              <GiftCard gift={g} highlight={g.purchase_id === purchaseId} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function GiftCard({ gift, highlight }: { gift: SkillcardGift; highlight?: boolean }) {
  const [copied, setCopied] = useState(false);
  const url = giftUrl(gift.code);
  const redeemed = !!gift.redeemed_at;

  async function copy() {
    setCopied(await copyText(`${formatGiftCode(gift.code)} — ${url}`));
    window.setTimeout(() => setCopied(false), 2500);
  }

  return (
    <div
      className={`ocean:bg-white/5 space-y-3 rounded-2xl border bg-white p-4 ${
        highlight ? 'border-accent-300 ring-accent-200 ring-2' : 'border-ink-100'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-ink-900 min-w-0 font-semibold">
          <MathText>{giftLotLabel(gift.kind, gift.label, gift.card_count)}</MathText>
        </p>
        <span
          className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            redeemed ? 'bg-ink-100 text-ink-600' : 'bg-green-100 text-green-700'
          }`}
        >
          {redeemed ? 'Activé' : 'À offrir'}
        </span>
      </div>
      <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-center">
        {!redeemed && (
          <QrCodeImage
            value={url}
            size={144}
            alt={`QR code du cadeau ${formatGiftCode(gift.code)}`}
          />
        )}
        <div className="w-full space-y-2 text-center sm:text-left">
          <p className="text-ink-500 text-xs font-bold uppercase tracking-wider">Code cadeau</p>
          <p className="font-display text-ink-900 text-2xl font-extrabold tracking-widest">
            {formatGiftCode(gift.code)}
          </p>
          {redeemed && gift.redeemed_at && (
            <p className="text-ink-500 text-xs">
              Activé le {new Date(gift.redeemed_at).toLocaleDateString('fr-FR')}
            </p>
          )}
        </div>
      </div>
      {!redeemed && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Button
            fullWidth
            onClick={() =>
              void shareContent({ title: 'Un cadeau pour toi', text: giftShareText(gift), url })
            }
          >
            <Share2 className="h-4 w-4" aria-hidden /> Partager
          </Button>
          <a
            href={giftMailto(gift)}
            className="border-ink-200 text-ink-800 inline-flex min-h-[2.75rem] items-center justify-center gap-2 rounded-xl border px-4 text-sm font-semibold active:scale-[0.98]"
          >
            <Mail className="h-4 w-4" aria-hidden /> Email
          </a>
          <Button variant="outline" fullWidth onClick={() => void copy()}>
            {copied ? (
              <Check className="h-4 w-4" aria-hidden />
            ) : (
              <Copy className="h-4 w-4" aria-hidden />
            )}{' '}
            {copied ? 'Copié' : 'Copier'}
          </Button>
        </div>
      )}
    </div>
  );
}
